import SECTION_NAMES from 'constants/sectionNames';
import { useAffordaPortalDetailsContext } from 'contexts/AffordaPortalDetailsContext';
import { useSectionsProgressContext } from 'contexts/SectionsProgressContext';
import useEnableAllSections from 'hooks/dev/useEnableAllSections';
import useHaveAllHouseholdMembersSignedConsentQuery from 'hooks/queries/useHaveAllHouseholdMembersSignedConsentQuery';
import useScreeningStatusQuery from 'hooks/queries/useScreeningStatusQuery';
import useIsUnInvitedSecondaryUser from 'hooks/useIsUnInvitedSecondaryUser';
import useDocumentationProgress from 'pages/AffordaDashboard/hooks.useDocumentationProgress';
import useUnInvitedSecondaryUserRouteManager from 'pages/AffordaDashboard/hooks.useUnInvitedSecondaryUserRouteManager';
import { useIsStudentDisqualified } from 'pages/AffordaHousehold/AffordaHousehold.FullTimeStudent.hooks';
import useAffordaApplication from 'pages/AffordaApplication/hooks';
import useHouseholdMemberStatusQuery from 'hooks/queries/useHouseholdMemberStatusQuery';
import { HOUSEHOLD_STATUS_DENIED } from 'utils/constants/household';
import useManageProps from './hooks.useManageProps';
import useManageFormCompletion from './hooks.useManageFormCompletion';

const useAffordaDashboard = () => {
  const { allSectionsEnabled } = useEnableAllSections();

  const { isLoading: isAffordaDetailsLoading } =
    useAffordaPortalDetailsContext();

  const { isInitialized, sectionsProgress } = useSectionsProgressContext();

  const {
    [SECTION_NAMES.APPLICATION]: applicationSectionProgress,
    [SECTION_NAMES.CREDIT_AND_CRIMINAL]: creditAndCriminalSectionProgress,
    [SECTION_NAMES.DOCUMENTATION]: documentationSectionProgress,
  } = sectionsProgress;

  const isUnInvitedSecondaryUser = useIsUnInvitedSecondaryUser();

  useUnInvitedSecondaryUserRouteManager({
    sectionsProgress,
    isSectionsProgressInitialized: isInitialized,
  });

  const {
    data: haveAllHouseholdMembersSignedConsent,
    isLoading: isConsentLoading,
  } = useHaveAllHouseholdMembersSignedConsentQuery();

  const { data: screeningStatus, isLoading: isScreeningStatusLoading } =
    useScreeningStatusQuery({
      options: { enabled: haveAllHouseholdMembersSignedConsent },
    });

  const { data: householdMemberStatus, isLoading: isHouseholdStatusLoading } =
    useHouseholdMemberStatusQuery();
  const isApplicationDenied =
    householdMemberStatus?.status === HOUSEHOLD_STATUS_DENIED;

  const {
    allMembersHaveCompletedDocumentation,
    allFilteredMembersDocumentationCompleted,
  } = useDocumentationProgress();

  // Check all the sections are completed for all the users.
  const { isIncomeAssetsNoEligible, isWelcomeAndQualifyUneligible } =
    useAffordaApplication();

  const isStudentDisqualified = useIsStudentDisqualified();

  // combines logic needed to determine props
  const { applicationProps, creditAndCriminalProps, documentationProps } =
    useManageProps({
      allFilteredMembersDocumentationCompleted,
      allMembersHaveCompletedDocumentation,
      allSectionsEnabled,
      applicationSectionProgress,
      creditAndCriminalSectionProgress,
      documentationSectionProgress,
      isStudentDisqualified,
      isUnInvitedSecondaryUser,
      screeningStatus,
    });

  // Manages when to set the primary/secondary forms as [complete: true]
  useManageFormCompletion({ sectionsProgress });

  const isDataLoaded =
    !isConsentLoading &&
    !isScreeningStatusLoading &&
    !isHouseholdStatusLoading &&
    !isAffordaDetailsLoading &&
    isInitialized;

  return {
    isDataLoaded,
    applicationProps,
    creditAndCriminalProps,
    documentationProps,
    isStudentDisqualified,
    isIncomeAssetsNoEligible,
    isApplicationDenied,
    isWelcomeAndQualifyUneligible,
  };
};

export default useAffordaDashboard;
